import React, { Component } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { getRemindManuallyReport, getReportDoctors, getVisitingDoctorsReport } from './../../logic/reports';
import { sortBy } from './../../libs/general';

const today = moment().format('YYYY-MM-DD');

// Wrapper component to get route parameters from React Router v6
function ReportWrapper(props) {
	const params = useParams();
	return <Report {...props} reportId={params.reportId} />;
}

class Report extends Component {
	constructor(props) {
		super(props);
		this.state = {
			items: [],
			date: today,
			docs: null,
			report: props.reportId || "1", // Get reportId from props
			sortFields: [], // Array to track sort fields in order of priority
			sortDirections: {} // Object to track sort direction for each field (true = ascending, false = descending)
		};
	}
	
	componentDidMount() {
		// Initialize report with the correct data
		this.handleDateChange({ target: { value: today } });
	}
	
	componentDidUpdate(prevProps) {
		// Update if reportId changes
		if (prevProps.reportId !== this.props.reportId) {
			this.setState({ report: this.props.reportId }, () => {
				this.handleDateChange({ target: { value: this.state.date } });
			});
		}
	}
	
	// Handle sorting by field
	handleSort = (field) => {
		const { sortFields, sortDirections } = this.state;
		let updatedFields = [...sortFields];
		let updatedDirections = {...sortDirections};
		
		// Check if field is already being sorted
		const index = updatedFields.indexOf(field);
		
		if (index !== -1) {
			// Field is already in sort fields
			if (updatedDirections[field]) {
				// If ascending, switch to descending
				updatedDirections[field] = false;
			} else {
				// If already descending, remove from sort fields
				updatedFields.splice(index, 1);
				delete updatedDirections[field];
			}
		} else {
			// Field is not in sort fields, add it
			updatedFields.push(field);
			updatedDirections[field] = true; // Default to ascending
		}
		
		// Update state with new sort fields and directions
		this.setState({ 
			sortFields: updatedFields,
			sortDirections: updatedDirections
		}, () => {
			// Apply sorting
			this.applySorting();
		});
	}
	
	// Apply multi-criteria sorting
	applySorting = () => {
		const { items, sortFields, sortDirections } = this.state;
		
		if (!items || items.length === 0) {
			return;
		}
		
		// If no sort fields, apply default sorting
		if (sortFields.length === 0) {
			let sorted = [...items];
			
			// Default sorting based on report type
			if (this.state.report === "2") {
				sorted.sort(sortBy('docName', false, null,
					sortBy('fromTimestamp', false, null)));
			} else {
				sorted.sort(sortBy('clinic', false, null,
					sortBy('fromTimestamp', false, null,
						sortBy('docId', false, null))));
			}
			
			this.setState({ items: sorted });
			return;
		}
		
		// Apply user-selected sorting
		let sorted = [...items];
		
		// Start with last sort field and work backwards for proper nesting
		for (let i = sortFields.length - 1; i >= 0; i--) {
			const field = sortFields[i];
			const isAscending = sortDirections[field];
			
			// Use as nested sorter if it's not the first one
			if (i < sortFields.length - 1) {
				const nextField = sortFields[i + 1];
				const nextIsAscending = sortDirections[nextField];
				sorted.sort(sortBy(field, !isAscending, null, 
					sortBy(nextField, !nextIsAscending)));
			} else {
				// Last sort field doesn't need nesting
				sorted.sort(sortBy(field, !isAscending));
			}
		}
		
		this.setState({ items: sorted });
	}
	
	// Get sort indicator for column headers
	getSortIndicator = (field) => {
		const { sortFields, sortDirections } = this.state;
		const index = sortFields.indexOf(field);
		
		if (index === -1) {
			return null;
		}
		
		const isAscending = sortDirections[field];
		const order = sortFields.length > 1 ? `${index + 1}` : '';
		
		return (
			<span className="sort-indicator">
				{order}{isAscending ? '▲' : '▼'}
			</span>
		);
	}

	getSortFieldName = (field) => {
		const fieldNames = {
			fromTimestamp: 'Date/Time',
			docName: 'Doctor',
			clinic: 'Clinic',
			description: 'Details',
			smsReminder: 'SMS Reminder',
			emailReminder: 'Email Reminder'
		};
		return fieldNames[field] || field;
	}
	
	handleDateChange = async (e) => {
		const date = e.target.value;
		const mDate = moment(e.target.value).format('YYYY-MM-DD');
		try {
			let docs = null;
			let items = null;

			if (this.state.report === "1") items = await getRemindManuallyReport(mDate);
			else if (this.state.report === "2") items = await getVisitingDoctorsReport(mDate);

			if (!this.state.docs) docs = await getReportDoctors();
			else docs = this.state.docs;

			if (items && items instanceof Array) {
				for (const item of items) {
					const { patient } = item;
					const descriptionArr = [];

					if (patient) {
						if (patient.name && patient.name.firstName) descriptionArr.push(patient.name.firstName);
						if (patient.name && patient.name.lastName) descriptionArr.push(patient.name.lastName);
						if (patient.idCard) descriptionArr.push(patient.idCard);
						if (patient.mobile && patient.mobile.mobileNumber) descriptionArr.push(patient.mobile.mobileNumber);
						if (patient.landline && patient.landline.landlineNumber) descriptionArr.push(patient.landline.landlineNumber);
						if (patient.email) descriptionArr.push(patient.email);
						if (patient.dob) descriptionArr.push(patient.dob);
					}
					item.description = descriptionArr.map((t) => t.toString().substring(0, 1).toUpperCase() + t.toString().substring(1, t.toString().length)).join(' - ');

					if (item.docId && docs) {
						const doc = docs.find((d) => d.docId === item.docId);

						if (doc) {
							const docNameArr = [];

							if (doc.title) docNameArr.push(doc.title + '.');
							if (doc.name && doc.name.firstName) docNameArr.push(doc.name.firstName);
							if (doc.name && doc.name.lastName) docNameArr.push(doc.name.lastName);

							item.docName = docNameArr.map((t) => t.toString().substring(0, 1).toUpperCase() + t.toString().substring(1, t.toString().length)).join(' ');
						}
					}

					if (item && item.reminders) {
						if (item.reminders.email) {
							switch (item.reminders.email.lastStatus) {
								case "SENT":
									item.emailReminder = 'SENT';
									if (item.reminders.email.lastSentTimestamp)
										item.emailReminder += ` on ${moment(item.reminders.email.lastSentTimestamp).format('DD MMM YYYY HH:mm')}`;
									break;
								case "ERROR":
									item.emailReminder = 'ERROR OCCURED WHEN SENDING';
									break;
								default:
									item.emailReminder = 'NEVER SENT';
							}
						} else item.emailReminder = 'NEVER SENT';
						if (item.reminders.sms) {
							switch (item.reminders.sms.lastStatus) {
								case "SENT":
									item.smsReminder = 'SENT';
									if (item.reminders.sms.lastSentTimestamp)
										item.smsReminder += ` on ${moment(item.reminders.sms.lastSentTimestamp).format('DD MMM YYYY HH:mm')}`;
									break;
								case "ERROR":
									item.smsReminder = 'ERROR OCCURED WHEN SENDING';
									break;
								default:
									item.smsReminder = 'NEVER SENT';
							}
						} else item.smsReminder = 'NEVER SENT';
					} else {
						item.emailReminder = 'NEVER SENT';
						item.smsReminder = 'NEVER SENT';
					}
				};

				// Apply different sorting based on the report type
				if (this.state.report === "2") { // Visiting doctors report
					// Sort first by doctor name, then by date/time
					items.sort(sortBy('docName', false, null,
						sortBy('fromTimestamp', false, null)));
				} else {
					// Keep original sorting for other reports
					items.sort(sortBy('clinic', false, null,
						sortBy('fromTimestamp', false, null,
							sortBy('docId', false, null))));
				}
				
				this.setState({ date, items });
			}

		} catch (error) {
			console.log(error);
		}

	}
	render() {
		return (
			<div>
				<div className="pages__back-button--container">
					<FontAwesomeIcon className="pages__back-button--icon" icon="chevron-left"></FontAwesomeIcon>
					<Link className="pages__back-button--text" to="/settings/reports">Back</Link>
				</div>
				<header className="pages__header--container centered-text">
					{
						this.state.report === "1" ?
							<h1>Remind Manually Report</h1>
							: <h1>Visiting Doctors Report</h1>
					}
				</header>
				<div className="reports__date">
					<span>Report date:&nbsp;&nbsp;</span>
					<input
						defaultValue={this.state.date}
						onChange={this.handleDateChange}
						type="date" />
				</div>
				<div>
					{
						this.state.items && this.state.items.length > 0 ?
							<>
								<div className="sorting-info">
									{this.state.sortFields.length > 0 ? (
										<p>Sorting by: {this.state.sortFields.map((field, i) => 
											`${i+1}. ${this.getSortFieldName(field)} (${this.state.sortDirections[field] ? 'ascending' : 'descending'})`
										).join(', ')} 
										<button className="btn btn-sm btn-link" onClick={() => this.setState({ sortFields: [], sortDirections: {} }, this.applySorting)}>
											Clear sorting
										</button>
										</p>
									) : (
										<p>Default sorting: {this.state.report === "2" ? 'Doctor name then Date/Time' : 'Clinic then Date/Time'}</p>
									)}
								</div>
								<table className="reports__container">
									<tbody>
										<tr>
											<th onClick={() => this.handleSort('fromTimestamp')} className="sortable-header">
												Date {this.getSortIndicator('fromTimestamp')}
											</th>
											<th onClick={() => this.handleSort('fromTimestamp')} className="sortable-header">
												Time {this.getSortIndicator('fromTimestamp')}
											</th>
											<th onClick={() => this.handleSort('docName')} className="sortable-header">
												Doctor {this.getSortIndicator('docName')}
											</th>
											<th onClick={() => this.handleSort('clinic')} className="sortable-header">
												Clinic {this.getSortIndicator('clinic')}
											</th>
											<th onClick={() => this.handleSort('description')} className="sortable-header">
												Details {this.getSortIndicator('description')}
											</th>
											<th onClick={() => this.handleSort('smsReminder')} className="sortable-header">
												SMS Reminder {this.getSortIndicator('smsReminder')}
											</th>
											<th onClick={() => this.handleSort('emailReminder')} className="sortable-header">
												Email Reminder {this.getSortIndicator('emailReminder')}
											</th>
										</tr>
										{
											this.state.items.map((item, i) => {
												return (
													<tr	key={i}>
														<td>{moment(item.fromTimestamp).format('DD MMM YYYY')}</td>
														<td>{`${moment(item.fromTimestamp).format('HH:mm')} to ${moment(item.toTimestamp).format('HH:mm')}`}</td>
														<td>{item.docName}</td>
														<td>{item.clinic}</td>
														<td>{item.description}</td>
														<td>{item.smsReminder}</td>
														<td>{item.emailReminder}</td>
													</tr>
												)
											})
										}
									</tbody>
								</table>
							</>
							: <div className="reports__nodata"><p>Report has no data</p></div>
					}
				</div>
				<div className="large-push"></div>
			</div>
		);
	}
}

export default ReportWrapper;